import _ from 'lodash';
import { Grid, Box, Avatar, Button, Chip, Typography } from '@mui/material';
import { Recycling } from '@mui/icons-material';

import { getCookie, setCookie } from 'utils/cookie';
import { DAY_IN_MILLISECONDS } from 'constants/common';
import { VERIFICATION_INTENT } from 'constants/verification';
import { RECYCLING_CHIP_BG, RECYCLING_CHIP_BORDER, RECYCLING_CHIP_TEXT, RECYCLING_CHIP_ICON } from 'constants/colors';

function ShipmentInfo(props) {
  const {
    store,
    name,
    logo,
    classes,
    mediaQuery,
    shipment,
    isDelivered,
    trackingCode,
    canTip,
    preferredWindow,
    originalFromTime,
    originalToTime,
    deliveryTimeAndLabel,
    useDriverFeedBack,
    hideCompanyBranding,
    enableRedPastDate,
    hasRecyclingEvent,
  } = props;

  const shouldHighlightDate = enableRedPastDate && deliveryTimeAndLabel.isPastDate;

  const setToken = (valToken, time, step) => {
    setCookie('axl_shipment_token', valToken, time);
    if (mediaQuery.isMobile && step < 4) {
      store.podStore.startVerification(VERIFICATION_INTENT.FEEDBACK);
      return;
    }
    store.podStore.updateField('currentStep', step);
    store.deliveryStore.openDialog(4);
  };

  const handleGiveUsDriverFeedback = () => {
    if (mediaQuery.isMobile && store.podStore.currentStep < 4) {
      store.podStore.startVerification(VERIFICATION_INTENT.FEEDBACK);
      return;
    }
    store.deliveryStore.openDialog(4);
  };

  const handleGiveUsFeedback = () => {
    try {
      store.deliveryStore.getFeedback();
      store.podStore.updateField('isPODFeedback', false);
      const token = getCookie('axl_shipment_token');

      if (!token) {
        setToken('', 0, 1);
        store.podStore.setImagesPOD([]);
        return;
      }
      store.deliveryStore.getTokenInfo(token, (res) => {
        if (res.ok) {
          const ttl = _.get(res, 'data.ttl', 0);
          const isPodScopes = _.get(res, 'data.scopes', []).includes('pod');
          if (isPodScopes) {
            setToken(token, ttl / DAY_IN_MILLISECONDS, 1);
            return;
          }
          setToken(token, ttl / DAY_IN_MILLISECONDS, 4);
          try {
            store.deliveryStore.getPOD((res) => {
              // Validate that res.data is an array before setting it
              const images = Array.isArray(res.data) ? res.data : [];
              store.podStore.setImagesPOD(images);
            });
          } catch (error) {
            /* empty */
          }
        } else {
          setToken('', 0, 1);
          store.podStore.setImagesPOD([]);
        }
      });
    } catch (error) {
      store.podStore.setImagesPOD([]);
    }
  };

  return (
    <Grid container sx={classes.shipmentInfoContainer}>
      <Grid size={{ xs: 12, sm: 6 }} sx={classes.shipmentInfoLeft}>
        <Grid container wrap="nowrap" spacing={2} style={{ marginBottom: 15 }}>
          {!hideCompanyBranding && (
            <Grid>
              <Avatar alt="Client Logo" src={logo} variant="square" sx={classes.clientLogo} />
            </Grid>
          )}
          <Grid>
            {!hideCompanyBranding && (
              <Typography sx={{ ...classes.clientName, ...classes.textOnSurfaceTextTitle }} variant="h4">
                {name}
              </Typography>
            )}
            <Box style={{ fontSize: mediaQuery.isMobile ? 14 : 'inherit' }}>
              <Box component="span" sx={classes.onSurfaceTextLabel}>
                Tracking code:{' '}
              </Box>
              <Box component="span" sx={classes.textOnSurfaceTextTitle} data-testid="shipment-info-tracking-code">
                {trackingCode}
              </Box>
            </Box>
            {hasRecyclingEvent && (
              <Chip
                icon={<Recycling fontSize="small" />}
                label="Recycling pickup"
                size="medium"
                variant="outlined"
                data-testid="shipment-info-recycling-badge"
                sx={{
                  marginTop: 1,
                  backgroundColor: RECYCLING_CHIP_BG,
                  border: `1px solid ${RECYCLING_CHIP_BORDER}`,
                  color: RECYCLING_CHIP_TEXT,
                  '& .MuiChip-icon': { color: RECYCLING_CHIP_ICON },
                  fontWeight: 700,
                  fontSize: 16,
                  paddingInline: 1,
                  borderRadius: 1,
                  height: '38px',
                }}
              />
            )}
          </Grid>
        </Grid>
        <Box mb={1} display={'flex'} flexDirection={{ sm: 'column', md: 'row' }} gap={1}>
          {isDelivered && !useDriverFeedBack && (
            <Button
              onClick={handleGiveUsFeedback}
              sx={{ ...classes.button, ...classes.hideSmall }}
              variant="contained"
              color="primary"
              disableElevation
            >
              Give us feedback!
            </Button>
          )}
          {isDelivered && useDriverFeedBack && (
            <Button
              onClick={handleGiveUsDriverFeedback}
              sx={{ ...classes.button, ...classes.hideSmall }}
              variant="contained"
              color="primary"
              disableElevation
            >
              Give us driver feedback!
            </Button>
          )}
          {isDelivered && canTip && (
            <Button
              color="primary"
              variant="outlined"
              sx={{ ...classes.button, ...classes.hideSmall }}
              onClick={() => store.deliveryStore.openDialog(5)}
              disableElevation
            >
              Leave a Tip
              <span role="img" aria-label="" style={{ marginLeft: '4px' }}>
                😃
              </span>
            </Button>
          )}
        </Box>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={classes.shipmentInfoRight}>
        <Box pl={mediaQuery.isMobile ? 8.25 : 0}>
          {preferredWindow ? (
            <Box style={{ marginBottom: 15 }}>
              <Box sx={{ ...classes.textOnSurfaceTextLabel, marginBottom: '10px' }}>Preferred Delivery on:</Box>
              <Box sx={classes.textOnSurfaceTextTitle} data-testid="shipment-info-prefer-deliver-on">
                {preferredWindow}
              </Box>
              <Box mt={1}>
                <small>{`* Original Delivery Window: ${originalFromTime} - ${originalToTime}`}</small>
              </Box>
            </Box>
          ) : (
            <Box style={{ marginBottom: 15 }}>
              <Box sx={{ ...classes.textOnSurfaceTextLabel, marginBottom: '10px' }}>{deliveryTimeAndLabel.label}</Box>
              <Box sx={{ ...classes.textOnSurfaceTextTitle, ...(shouldHighlightDate && classes.redText) }}>{deliveryTimeAndLabel.time}</Box>
            </Box>
          )}
          <Box>
            <Box sx={{ ...classes.textOnSurfaceTextLabel, marginBottom: '10px' }}>Deliver to:</Box>
            <Box sx={classes.textOnSurfaceTextTitle} data-testid="shipment-info-deliver-to">{`${
              shipment.dropoff_address.city
            }, ${(shipment.dropoff_address.state || '').toUpperCase()}${
              shipment.dropoff_address.zipcode ? `, ${shipment.dropoff_address.zipcode}` : ''
            }`}</Box>
          </Box>
        </Box>
      </Grid>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }} gap={1}>
        {isDelivered && !useDriverFeedBack && (
          <Button
            onClick={handleGiveUsFeedback}
            sx={{ ...classes.button, ...classes.showSmall }}
            fullWidth
            variant="contained"
            color="primary"
            disableElevation
          >
            Give us feedback!
          </Button>
        )}
        {isDelivered && useDriverFeedBack && (
          <Button
            onClick={handleGiveUsDriverFeedback}
            sx={{ ...classes.button, ...classes.showSmall }}
            fullWidth
            variant="contained"
            color="primary"
            disableElevation
          >
            Give us driver feedback!
          </Button>
        )}
        {isDelivered && canTip && (
          <Button
            color="primary"
            variant="outlined"
            fullWidth
            sx={{ ...classes.button, ...classes.showSmall }}
            onClick={() => store.deliveryStore.openDialog(5)}
            disableElevation
          >
            Leave a Tip
            <span role="img" aria-label="" style={{ marginLeft: '4px' }}>
              😃
            </span>
          </Button>
        )}
      </Box>
    </Grid>
  );
}

export default ShipmentInfo;
